import React from 'react';
import { Package, Building, Home, Sparkles, Check, MapPin } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { services, businessInfo } from '../mock';

const iconMap = {
  Package,
  Building,
  Home,
  Sparkles
};

const Services = () => {
  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <p className="text-lg text-gray-600">
            Professional Wash & Fold with FREE Pickup & Delivery
          </p>
        </div>

        {/* Main Wash & Fold Highlight */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-3xl p-8 mb-12 text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              🧺 Wash & Fold
            </h3>
            <div className="text-4xl sm:text-5xl font-bold text-teal-600 mb-2">
              $1.50/lb
            </div>
            <div className="text-xl text-gray-700 mb-4">
              $15 minimum order
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-gray-600">
              <span className="flex items-center gap-2">
                <Check size={18} className="text-teal-500" />
                FREE Pickup & Delivery
              </span>
              <span className="flex items-center gap-2">
                <Check size={18} className="text-teal-500" />
                Same-Day Service Available
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Serving Snellville • Lilburn • Lawrenceville • Norcross
            </p>
          </div>
        </div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) => {
            const Icon = iconMap[service.icon];
            
            return (
              <Card 
                key={service.id}
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-2 ${
                  service.active ? 'border-teal-200 border-2' : 'border-gray-200'
                }`}
              >
                {service.comingSoon && (
                  <Badge className="absolute top-4 right-4 bg-amber-500 hover:bg-amber-600">
                    Coming Soon
                  </Badge>
                )}
                
                {service.active && (
                  <Badge className="absolute top-4 right-4 bg-teal-500 hover:bg-teal-600">
                    Available Now
                  </Badge>
                )}
                
                <CardHeader>
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                    service.active 
                      ? 'bg-gradient-to-br from-teal-500 to-cyan-500' 
                      : 'bg-gray-200'
                  }`}>
                    <Icon className="text-white" size={28} />
                  </div>
                  <CardTitle className="text-xl">{service.name}</CardTitle>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-4">
                    {service.active ? (
                      <div>
                        {/* Direct Website Price - Main pricing */}
                        <div className="bg-teal-50 rounded-lg p-4 mb-3">
                          <div className="text-xs text-teal-700 font-medium mb-1">DIRECT WEBSITE ORDERS</div>
                          <div className="text-2xl font-bold text-teal-700">
                            {service.price}
                          </div>
                          {service.priceSubtext && (
                            <div className="text-sm text-teal-600">
                              {service.priceSubtext}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-lg font-semibold text-gray-600">
                        {service.price}
                      </div>
                    )}
                    
                    <ul className="space-y-2">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                          <Check className="text-teal-500 flex-shrink-0 mt-0.5" size={16} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Flat Rate Packages */}
        <div className="mt-12 bg-white rounded-3xl border-2 border-gray-100 p-8">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
            Flat Rate Packages
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-lg font-bold text-gray-900">Small Load</div>
              <div className="text-2xl font-bold text-teal-600">$15</div>
              <div className="text-sm text-gray-500">Up to 10 lbs</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-lg font-bold text-gray-900">Medium Load</div>
              <div className="text-2xl font-bold text-teal-600">$30</div>
              <div className="text-sm text-gray-500">Up to 20 lbs</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-lg font-bold text-gray-900">Large Load</div>
              <div className="text-2xl font-bold text-teal-600">$45</div>
              <div className="text-sm text-gray-500">Up to 30 lbs</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-lg font-bold text-gray-900">Extra Large</div>
              <div className="text-2xl font-bold text-teal-600">$75</div>
              <div className="text-sm text-gray-500">Up to 50 lbs</div>
            </div>
          </div>
        </div>

        {/* Pricing Disclaimer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 italic max-w-2xl mx-auto">
            Pricing is based on clean, dry weight. Bulky items such as comforters, blankets, rugs, and oversized items are priced separately.
          </p>
        </div>

        {/* Drop-off Location */}
        <div className="mt-12 bg-blue-50 rounded-3xl p-8 text-center">
          <div className="max-w-2xl mx-auto">
            <MapPin size={32} className="mx-auto text-blue-600 mb-4" />
            <h3 className="text-xl sm:text-2xl font-bold mb-2 text-gray-800">
              Wash & Fold Drop-Off Location
            </h3>
            <p className="text-lg text-blue-700 font-semibold mb-2">
              {businessInfo.address}
            </p>
            <p className="text-gray-600">
              Same great service • Same great prices • Skip the pickup wait
            </p>
          </div>
        </div>

        {/* Loyalty program highlight */}
        <div className="mt-8 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-3xl p-8 sm:p-12 text-white text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">
              Loyalty Rewards Program
            </h3>
            <p className="text-lg sm:text-xl mb-6">
              Get your <span className="font-bold text-yellow-300">11th Wash & Fold service FREE</span> after completing 10 services!
            </p>
            <p className="text-sm opacity-90">
              Direct website Wash & Fold orders only. Minimum order $15/service value applies. See terms for details.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
